import '../../modules/finance/components/financeExperience.css';
import DateRangeFilter,{useDateRange} from '../common/DateRangeFilter';
import useTenantTimeZone from '../common/useTenantTimeZone';
import {inPeriod,validRange} from '../../utils/financeDateRange';
import React, { useEffect, useRef, useState } from 'react';
import { loadWorkerEarnings, observeEarningsIdentity } from './workerEarningsService';

const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
const number = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const date = (value, options) => value && Number.isFinite(new Date(value).getTime()) ? new Date(value).toLocaleString([], options) : 'Unavailable';

export default function WorkerEarnings({ workerUid, tenantId }) {
  return <EarningsSession key={(workerUid || 'signed-out')+':'+(tenantId||'')} workerUid={workerUid} tenantId={tenantId} />;
}
function EarningsSession({ workerUid, tenantId }) {
  const timeZone=useTenantTimeZone(tenantId);
  const range=useDateRange('this_month',timeZone);
  const [records, setRecords] = useState([]), [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true), [error, setError] = useState(false), [identity, setIdentity] = useState(null);
  const generation = useRef(0);
  const identityRef = useRef(null);
  async function refresh(next = null) {
    const request = ++generation.current;
    setLoading(true); setError(false);
    if (!next) { setRecords([]); setCursor(null); }
    try {
      const result = await loadWorkerEarnings(workerUid, next);
      if (request !== generation.current || identityRef.current !== workerUid) return;
      if (!Array.isArray(result?.records) || result.records.some(row => row.workerUid !== workerUid)) throw new Error('Invalid earnings response');
      setRecords(previous => next ? [...new Map([...previous, ...result.records].map(row => [row.shiftId, row])).values()] : result.records);
      setCursor(result.nextCursor || null);
    } catch (_) {
      if (request === generation.current) { setError(true); setRecords([]); setCursor(null); }
    } finally { if (request === generation.current) setLoading(false); }
  }
  useEffect(() => {
    const stop = observeEarningsIdentity(uid => {
      ++generation.current; identityRef.current = uid; setIdentity(uid); setRecords([]); setCursor(null);
      if (uid && uid === workerUid) refresh(); else setLoading(false);
    });
    const focus = () => { if (identityRef.current === workerUid && workerUid) refresh(); };
    window.addEventListener('focus', focus);
    return () => { ++generation.current; stop(); window.removeEventListener('focus', focus); };
    // A keyed session is remounted for every worker; refresh uses that identity.
  }, [workerUid]);
  if (!identity || identity !== workerUid) return <section className="we-empty"><p>Sign in as a worker to view earnings.</p></section>;
  const visible=records.filter(row=>inPeriod(row,range,timeZone));
  const sorted = [...visible].sort((a, b) => (Date.parse(b.startTime) || 0) - (Date.parse(a.startTime) || 0));
  return <section className="earnings-center" aria-label="Worker earnings" aria-busy={loading}>
    <header className="finance-heading"><div><p>A personal record of completed shifts and payment status.</p></div></header>
    <DateRangeFilter value={range} timeZone={timeZone}/>
    <p className="finance-note">Straight-time base earnings. Overtime is not included.</p>
    <button type="button" disabled={loading} onClick={() => refresh()}>Refresh earnings</button>
    {loading && <p role="status">Loading earnings…</p>}
    {error ? <div className="we-empty" role="alert">Earnings details are not yet available. Please try again.</div> : <>
      {!loading && !records.length && !cursor ? <div className="we-empty">No earnings have been generated yet.</div> : <>
        {records.length > 0 && <div className="finance-summary">
          <section><h2>Total Earnings</h2><strong>{!!timeZone && validRange(range) && !cursor && visible.every(row => number(row.grossEarnings)) ? money(visible.reduce((sum,row) => sum + row.grossEarnings,0)) : "Awaiting payroll calculation"}</strong></section>
          <section><h2>Completed shifts</h2><strong>{visible.length}{cursor ? "+" : ""}</strong><span className="finance-note">Completed records</span></section>
          {['Pending', 'Paid'].map(status => {
            const rows = visible.filter(row => row.status === status);
            const complete = !!timeZone && validRange(range) && !cursor && rows.every(row => number(row.grossEarnings));
            return <section className="we-surface" key={status}><h2>{status} Earnings</h2><p>{complete ? money(rows.reduce((sum, row) => sum + row.grossEarnings, 0)) : rows.length ? 'Amount pending payroll processing' : 'No records loaded'}</p></section>;
          })}
        </div>}
        <h2>Recent Earnings</h2>
        {cursor && <p>More records are available. Summary amounts remain unavailable until all records are loaded.</p>}
        {!loading && !sorted.length && <p>No earnings in the selected period.</p>}
        {sorted.map(row => <article className="we-surface" key={row.shiftId} style={{ marginBottom: 12, overflowWrap: 'anywhere' }}>
          <h3>{row.facilityName || row.facilityId}</h3><p>{row.role || 'Role unavailable'}</p>
          <p>{date(row.startTime, { timeZone, year: 'numeric', month: 'short', day: 'numeric' })}</p>
          <p>{date(row.startTime, { timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} – {date(row.endTime, { timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}</p>
          {number(row.hours) && <p>{row.hours} payable hours</p>}
          {number(row.workerPayRate) && <p>{money(row.workerPayRate)}/hr</p>}
          <p>{number(row.grossEarnings) ? `Gross: ${money(row.grossEarnings)}` : 'Earnings amount pending payroll processing.'}</p>
          <p className={`finance-pill ${row.status === "Paid" ? "paid" : ""}`}>Status: {row.status === 'Paid' ? 'Paid' : 'Pending'}</p>
          {row.paidAt && <p>Paid: {date(row.paidAt, { timeZone, year: 'numeric', month: 'short', day: 'numeric' })}</p>}
        </article>)}
        {cursor && <button type="button" disabled={loading} onClick={() => refresh(cursor)}>Load more earnings</button>}
      </>}
    </>}
  </section>;
}
